import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAngularContext } from 'AngularContext';
import { useLazyGetEvaluationModalDataQuery, type ProjectSubmission } from '../api';
import {
    openEvaluationModalCore,
    type AngularInjector,
    type UseEvaluationModalResult,
    getErrorMessage,
    EVALUATION_MODAL_ERRORS,
} from '../shared';

interface UseEvaluationModalOptions {
    /** Callback to run when modal closes (regardless of whether changes were saved) */
    onClose?: () => void;
}

/**
 * Hook that provides a function to open the evaluation modal for a project submission.
 * Uses the admin endpoint which requires admin privileges.
 *
 * @param options - Optional configuration including onClose callback
 * @returns Object with openEvaluationModal function, isLoading state, error state, and clearError function
 */
export function useEvaluationModal(options?: UseEvaluationModalOptions): UseEvaluationModalResult {
    const $injector = useAngularContext();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fetchEvaluationData] = useLazyGetEvaluationModalDataQuery();
    const inFlightRef = useRef(false);

    // Use a ref to store the onClose callback to avoid stale closure issues.
    // This ensures the callback passed to DialogModal is always the latest version.
    const onCloseRef = useRef(options?.onClose);
    useEffect(() => {
        onCloseRef.current = options?.onClose;
    }, [options?.onClose]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const openEvaluationModal = useCallback(
        async (submission: ProjectSubmission) => {
            // Clear any previous error
            setError(null);

            if (!submission.cohortName?.id) {
                setError(EVALUATION_MODAL_ERRORS.NO_COHORT);
                return;
            }

            // Prevent rapid double-clicks during loading
            if (inFlightRef.current) return;
            inFlightRef.current = true;
            setIsLoading(true);

            try {
                // Fetch data from the admin API endpoint
                const result = await fetchEvaluationData({
                    userId: submission.student.id,
                    cohortId: submission.cohortName.id,
                    requirementIdentifier: submission.requirementIdentifier,
                }).unwrap();

                // Use the shared core function to open the modal.
                // Wrap onClose to read from onCloseRef, avoiding stale closures.
                await openEvaluationModalCore({
                    submission,
                    $injector: $injector as AngularInjector,
                    dispatch,
                    fetchResult: result,
                    onClose: () => onCloseRef.current?.(),
                });
            } catch (err) {
                setError(getErrorMessage(err, EVALUATION_MODAL_ERRORS.FETCH_FAILED));
            } finally {
                setIsLoading(false);
                inFlightRef.current = false;
            }
        },
        [$injector, dispatch, fetchEvaluationData],
    );

    return { openEvaluationModal, isLoading, error, clearError };
}
